/**
 * ApproachA - 2クリック方式のセクション分類 UI
 *
 * PoC frontend/src/section-classifier/ApproachA.tsx から移植。
 * production 用に isSubmitting prop と必須 onConfirm を追加。
 *
 * 2 カラムレイアウト:
 * - 左: 発話一覧（クリックで始点/終点を設定）
 * - 右: セクションパネル（セクション選択 + ステータス表示）
 */

import { useState, useCallback, useMemo } from 'react';
import { List, useDynamicRowHeight, type RowComponentProps } from 'react-window';
import { UtteranceRow } from './components/UtteranceRow';
import { SectionPanel } from './components/SectionPanel';
import { ConfirmBar } from './components/ConfirmBar';
import type { SectionAssignment, SectionDefinition, TranscriptUtterance } from './types';

type ClickPhase = 'idle' | 'start' | 'end';

interface ApproachAProps {
  utterances: TranscriptUtterance[];
  sections: SectionDefinition[];
  onConfirm: (assignments: SectionAssignment[]) => void;
  isSubmitting?: boolean;
}

/**
 * react-window の rowProps として渡すスタブルなコンテキスト。
 * 各 row はこのオブジェクトと自身の index から表示状態を導出する。
 */
interface UtteranceListRowProps {
  utterances: TranscriptUtterance[];
  utteranceAssignmentMap: (SectionAssignment | null)[];
  sectionById: Map<string, SectionDefinition>;
  activeSection: SectionDefinition | null;
  activeSectionId: string | null;
  clickPhase: ClickPhase;
  pendingStartIndex: number | null;
  maxEndForPending: number | null;
  onUtteranceClick: (index: number) => void;
}

function UtteranceListRow({
  index,
  style,
  utterances,
  utteranceAssignmentMap,
  sectionById,
  activeSection,
  activeSectionId,
  clickPhase,
  pendingStartIndex,
  maxEndForPending,
  onUtteranceClick,
}: RowComponentProps<UtteranceListRowProps>) {
  const utterance = utterances[index];
  if (!utterance) return null;
  const assignment = utteranceAssignmentMap[index] ?? null;
  const section = assignment ? sectionById.get(assignment.sectionId) ?? null : null;
  const isBlockStart = !!assignment && assignment.startUtteranceIndex === index;
  const isBlockEnd = !!assignment && assignment.endUtteranceIndex === index;
  const isPendingStart = pendingStartIndex === index;

  let clickable = false;
  if (clickPhase === 'start') {
    clickable = !assignment || assignment.sectionId === activeSectionId;
  } else if (clickPhase === 'end' && pendingStartIndex !== null) {
    clickable = index >= pendingStartIndex && (maxEndForPending === null || index <= maxEndForPending);
  }

  const bgColor = section ? section.bgColor : undefined;
  const borderColor = section
    ? `border-l-4 ${section.borderColor}`
    : 'border-l-4 border-l-transparent';

  return (
    <div style={style}>
      {isBlockStart && section && (
        <div
          className={`px-3 py-1.5 text-xs font-semibold ${section.bgColor} ${section.textColor} border-y border-gray-200 flex items-center gap-2`}
        >
          {section.name} 開始
        </div>
      )}
      {isPendingStart && activeSection && (
        <div
          className={`px-3 py-1 text-xs font-semibold ${activeSection.bgColor} ${activeSection.textColor} border-y border-dashed border-current flex items-center gap-2`}
        >
          {activeSection.name} 開始位置（終了位置をクリック）
        </div>
      )}
      <UtteranceRow
        utterance={utterance}
        index={index}
        isHighlighted={isPendingStart}
        sectionColor={bgColor}
        borderColor={borderColor}
        onClick={onUtteranceClick}
        clickable={clickable}
      />
      {isBlockEnd && section && (
        <div
          className={`px-3 py-1 text-xs ${section.bgColor} ${section.textColor} border-b border-gray-200 flex items-center gap-2 opacity-70`}
        >
          {section.name} 終了
        </div>
      )}
    </div>
  );
}

export function ApproachA({
  utterances,
  sections,
  onConfirm,
  isSubmitting = false,
}: ApproachAProps) {
  const [assignments, setAssignments] = useState<SectionAssignment[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [clickPhase, setClickPhase] = useState<ClickPhase>('idle');
  const [pendingStartIndex, setPendingStartIndex] = useState<number | null>(null);

  const utteranceAssignmentMap = useMemo(() => {
    const map: (SectionAssignment | null)[] = new Array(utterances.length).fill(null);
    for (const a of assignments) {
      for (let i = a.startUtteranceIndex; i <= a.endUtteranceIndex; i++) {
        map[i] = a;
      }
    }
    return map;
  }, [assignments, utterances.length]);

  /**
   * sections の find を index ループで毎行回さないように
   * id → SectionDefinition の lookup を memoize。
   */
  const sectionById = useMemo(() => {
    const m = new Map<string, SectionDefinition>();
    for (const s of sections) m.set(s.id, s);
    return m;
  }, [sections]);

  const activeSection = activeSectionId ? sectionById.get(activeSectionId) ?? null : null;
  const activeAllowsMultiple = activeSection?.allowMultipleRanges === true;

  /**
   * 重複チェック。
   * 通常 section: 同じ sectionId の既存 assignment は「上書き」されるため除外する。
   * allowMultipleRanges section: 同じ sectionId でも複数 range を保持するため除外しない。
   */
  const isOverlapping = useCallback(
    (startIdx: number, endIdx: number, excludeSectionId?: string) => {
      return assignments.some(a => {
        const sec = sectionById.get(a.sectionId);
        const isMulti = sec?.allowMultipleRanges === true;
        if (a.sectionId === excludeSectionId && !isMulti) return false;
        return startIdx <= a.endUtteranceIndex && endIdx >= a.startUtteranceIndex;
      });
    },
    [assignments, sectionById],
  );

  /**
   * clickPhase === 'end' のとき、各 index が pendingStart 〜 ? の範囲内で
   * かつ既存 assignment と重なっていない最大 end index を 1 度だけ計算。
   * これで O(n × assignments) のチェックを O(n + assignments) に圧縮。
   */
  const maxEndForPending = useMemo(() => {
    if (clickPhase !== 'end' || pendingStartIndex === null) return null;
    // pendingStart より大きい assignment のうち、startUtteranceIndex が最小の値 - 1 が最大 end
    let maxEnd = utterances.length - 1;
    for (const a of assignments) {
      if (a.sectionId === activeSectionId) continue;
      if (a.startUtteranceIndex >= pendingStartIndex && a.startUtteranceIndex - 1 < maxEnd) {
        maxEnd = a.startUtteranceIndex - 1;
      }
    }
    return maxEnd;
  }, [clickPhase, pendingStartIndex, assignments, activeSectionId, utterances.length]);

  const handleUtteranceClick = useCallback(
    (index: number) => {
      if (!activeSectionId || isSubmitting) return;

      if (clickPhase === 'start') {
        const existingAtIndex = utteranceAssignmentMap[index];
        if (existingAtIndex) {
          // 既存 assignment の上に始点を打つのは、通常 section の場合の「上書き」のみ許容
          // multi-range section では既存 range の上には新規範囲を作れない
          if (activeAllowsMultiple) return;
          if (existingAtIndex.sectionId !== activeSectionId) return;
        }
        setPendingStartIndex(index);
        setClickPhase('end');
      } else if (clickPhase === 'end' && pendingStartIndex !== null) {
        if (index < pendingStartIndex) return;
        if (isOverlapping(pendingStartIndex, index, activeSectionId)) return;

        setAssignments(prev => {
          if (activeAllowsMultiple) {
            // multi-range: 既存 range は残し、新規 range を追加
            return [
              ...prev,
              {
                sectionId: activeSectionId,
                startUtteranceIndex: pendingStartIndex,
                endUtteranceIndex: index,
              },
            ];
          }
          // 通常: 同 section の既存 range を置き換え
          const filtered = prev.filter(a => a.sectionId !== activeSectionId);
          return [
            ...filtered,
            {
              sectionId: activeSectionId,
              startUtteranceIndex: pendingStartIndex,
              endUtteranceIndex: index,
            },
          ];
        });

        if (activeAllowsMultiple) {
          // multi-range section は同セクションに留まり、続けて追加できるようにする
          // 始点クリック待ちにリセット
          setClickPhase('start');
        } else {
          const currentSection = sections.find(s => s.id === activeSectionId);
          if (currentSection) {
            const nextSection = sections.find(
              s =>
                s.order > currentSection.order &&
                !assignments.some(a => a.sectionId === s.id),
            );
            if (nextSection) {
              setActiveSectionId(nextSection.id);
              setClickPhase('start');
            } else {
              setActiveSectionId(null);
              setClickPhase('idle');
            }
          }
        }
        setPendingStartIndex(null);
      }
    },
    [
      activeSectionId,
      activeAllowsMultiple,
      clickPhase,
      pendingStartIndex,
      utteranceAssignmentMap,
      isOverlapping,
      assignments,
      sections,
      isSubmitting,
    ],
  );

  const handleSectionSelect = useCallback(
    (sectionId: string) => {
      if (isSubmitting) return;
      if (activeSectionId === sectionId) {
        setActiveSectionId(null);
        setClickPhase('idle');
        setPendingStartIndex(null);
      } else {
        const sec = sectionById.get(sectionId);
        const isMulti = sec?.allowMultipleRanges === true;
        if (!isMulti) {
          // 通常 section: 再選択時に既存範囲をクリア（上書き再設定の動線を維持）
          setAssignments(prev => prev.filter(a => a.sectionId !== sectionId));
        }
        // multi-range section: 既存範囲は残して追加モードに入る
        setActiveSectionId(sectionId);
        setClickPhase('start');
        setPendingStartIndex(null);
      }
    },
    [activeSectionId, isSubmitting, sectionById],
  );

  const handleSectionRemove = useCallback((assignment: SectionAssignment) => {
    setAssignments(prev => prev.filter(a => !(
      a.sectionId === assignment.sectionId &&
      a.startUtteranceIndex === assignment.startUtteranceIndex &&
      a.endUtteranceIndex === assignment.endUtteranceIndex
    )));
  }, []);

  const handleRangeAdd = useCallback((sectionId: string) => {
    if (isSubmitting) return;
    const sec = sectionById.get(sectionId);
    if (sec?.allowMultipleRanges !== true) return;
    setActiveSectionId(sectionId);
    setClickPhase('start');
    setPendingStartIndex(null);
  }, [isSubmitting, sectionById]);

  const handleReset = useCallback(() => {
    setAssignments([]);
    setActiveSectionId(null);
    setClickPhase('idle');
    setPendingStartIndex(null);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(assignments);
  }, [assignments, onConfirm]);

  // react-window 用の動的行高さキャッシュ。デフォルトは 1 行発話の概算。
  // 行内に「開始/終了/開始位置」マーカーが含まれる場合は計測後にキャッシュ更新される。
  const rowHeight = useDynamicRowHeight({ defaultRowHeight: 56 });

  // List に渡すスタブルな rowProps。state 変化時のみ参照変わる。
  const rowProps = useMemo<UtteranceListRowProps>(
    () => ({
      utterances,
      utteranceAssignmentMap,
      sectionById,
      activeSection,
      activeSectionId,
      clickPhase,
      pendingStartIndex,
      maxEndForPending,
      onUtteranceClick: handleUtteranceClick,
    }),
    [
      utterances,
      utteranceAssignmentMap,
      sectionById,
      activeSection,
      activeSectionId,
      clickPhase,
      pendingStartIndex,
      maxEndForPending,
      handleUtteranceClick,
    ],
  );

  const guideMessage = useMemo(() => {
    if (!activeSectionId) return null;
    const section = sections.find(s => s.id === activeSectionId);
    if (!section) return null;
    const multiSuffix = section.allowMultipleRanges
      ? '（問題ごとに繰り返し追加できます。終わったら別のセクションを選択）'
      : '';
    if (clickPhase === 'start') {
      return {
        text: `「${section.name}」の開始位置をクリックしてください${multiSuffix}`,
        bgColor: section.bgColor,
        textColor: section.textColor,
      };
    }
    if (clickPhase === 'end') {
      return {
        text: `「${section.name}」の終了位置をクリックしてください`,
        bgColor: section.bgColor,
        textColor: section.textColor,
      };
    }
    return null;
  }, [activeSectionId, clickPhase, sections]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 flex overflow-hidden">
        <div className="flex-1 min-h-0 flex flex-col bg-white">
          {guideMessage && (
            <div
              className={`px-4 py-2 text-sm font-medium border-b ${guideMessage.bgColor} ${guideMessage.textColor}`}
            >
              {guideMessage.text}
            </div>
          )}

          <div className="flex-1 min-h-0">
            <List
              rowComponent={UtteranceListRow}
              rowCount={utterances.length}
              rowHeight={rowHeight}
              rowProps={rowProps}
              style={{ height: '100%', width: '100%' }}
              overscanCount={8}
            />
          </div>
        </div>

        <div className="w-72 bg-gray-50 border-l border-gray-200 flex flex-col">
          <SectionPanel
            sections={sections}
            assignments={assignments}
            utterances={utterances}
            activeSectionId={activeSectionId}
            clickPhase={clickPhase}
            onSectionSelect={handleSectionSelect}
            onSectionRemove={handleSectionRemove}
            onRangeAdd={handleRangeAdd}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>

      <ConfirmBar
        assignedCount={new Set(assignments.map(a => a.sectionId)).size}
        totalSections={sections.length}
        onConfirm={handleConfirm}
        onReset={handleReset}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
